import { useEffect, useRef, useState } from "react";
import BaseSearchClient, { BaseListResult } from "../BaseSearchClient";
import { EventListFilters } from "/Components/Pages/EventListPage/EventListPage";
import { NewsListFilters } from "/Components/Pages/NewsListPage/NewsListPage";

export interface BaseListFilters {}
export type FilterType = EventListFilters | NewsListFilters;

interface UseSearchPageOptions {
  client: BaseSearchClient;
  initFilters: FilterType;
  initList?: BaseListResult;
}

function useListPageFilters({
  client,
  initFilters,
  initList,
}: UseSearchPageOptions) {
  const [items, setItems] = useState(initList?.sectionItems);
  const [resultsCount, setResultsCount] = useState<number | undefined>();
  const [totalPages, setTotalPages] = useState(initList?.totalPages);
  const [filters, setFilters] = useState<FilterType>({
    ...initFilters,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [shouldPushState, setShouldPushState] = useState(true);

  const isMounted = useRef(false);

  const updateList = () => {
    client.GetItems({ ...filters }).then((result) => {
      if ("error" in result) {
        setError(result.message);
        setLoading(false);
      } else {
        setLoading(false);
        setItems(result.sectionItems);
        setResultsCount(countResults(result.sectionItems));
        setTotalPages(result.totalPages);
        if (shouldPushState) {
          const newUrl = client.BuildUrlFromProps({ ...filters });
          window.history.pushState(null, "", newUrl);
        }
        setShouldPushState(true);
      }
    });
  };

  const countResults = (sectionItems: any) => {
    if (!isMounted) return undefined;

    if (!sectionItems) return 0;

    return sectionItems.reduce((total: number, section: any) => {
      return total + section.items.length;
    }, 0);
  };

  const setTab = (upcomingSelected: boolean) => {
    setFilters({ pageNumber: 1, upcomingEvents: upcomingSelected });
    setLoading(true);
  };

  const setPageNumber = (pageNum: number, upcomingSelected?: boolean) => {
    setFilters(
      upcomingSelected
        ? { pageNumber: pageNum, upcomingEvents: upcomingSelected }
        : { pageNumber: pageNum },
    );
    setLoading(true);
  };

  useEffect(() => {
    if (loading) updateList();
  }, [loading]);

  useEffect(() => {
    isMounted.current ? setLoading(true) : (isMounted.current = true);
  }, [filters.pageNumber]);

  useEffect(() => {
    //called on mount. reads url parameters into state machine
    const handlePopState = () => {
      const urlParams = client.GetParams(filters);
      if (urlParams.pageNumber)
        setFilters({ pageNumber: urlParams.pageNumber });
      if (urlParams.upcomingEvents)
        setFilters({ upcomingEvents: urlParams.upcomingEvents });
      setShouldPushState(false);
      setLoading(true);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  return {
    items,
    resultsCount,
    loading,
    error,
    filters,
    totalPages,
    setError,
    setFilters,
    setPageNumber,
    setTab,
  };
}

export default useListPageFilters;
